import { call, put, delay } from 'redux-saga/effects';
import * as recipientActions from '../actions/recipients';
import * as API from './api';
import { sortRecipientsByNewest } from '../../utils/recipientHelpers';
import { getDocumentUploadValidationError } from '../../utils/documentUploadHelpers';

function* getRecipients() {
  try {
    const recipients = yield call(API.getRecipients);
    yield put({
      type: recipientActions.GET_RECIPIENTS_SUCCESS,
      payload: recipients,
    });
  } catch (error) {
    error.name = 'Could not get Recipients';
    yield put({ type: recipientActions.GET_RECIPIENTS_ERROR, payload: error });
  }
}

function* getRecipient({ payload }) {
  try {
    const recipient = yield call(API.getRecipient, payload);
    yield put({
      type: recipientActions.GET_RECIPIENT_SUCCESS,
      payload: recipient,
    });
  } catch (error) {
    error.name = 'Could not get Recipient';
    yield put({ type: recipientActions.GET_RECIPIENT_ERROR, payload: error });
  }
}

function* addRecipient({ payload }) {
  try {
    const { currencyCode, recipientType, rail, data } = payload;

    if (!currencyCode) {
      throw new Error('currencyCode is required');
    }
    if (!recipientType) {
      throw new Error('recipientType is required');
    }
    if (!rail) {
      throw new Error('rail is required');
    }
    if (!data || typeof data !== 'object') {
      throw new Error('data object is required');
    }

    yield call(API.addRecipient, currencyCode, recipientType, rail, data);

    yield delay(500);

    const recipients = yield call(API.getRecipients);
    yield put({
      type: recipientActions.GET_RECIPIENTS_SUCCESS,
      payload: recipients,
    });

    const newest = sortRecipientsByNewest(recipients || [])[0];
    yield put({
      type: recipientActions.ADD_RECIPIENT_SUCCESS,
      payload: newest?.id ? { recipientId: newest.id } : null,
    });
  } catch (error) {
    const errorPayload = {
      name: 'Could not create Recipient',
      message: error.message,
      statusCode: error.statusCode,
      traceId: error.traceId,
      response: error.response,
      data: error.data,
      ...error,
    };
    yield put({ type: recipientActions.ADD_RECIPIENT_ERROR, payload: errorPayload });
  }
}

function* deleteRecipient({ payload }) {
  try {
    const { offRampProvider, recipientId } = payload;
    if (!offRampProvider || !recipientId) {
      throw new Error('offRampProvider and recipientId are required');
    }
    yield call(API.deleteRecipient, offRampProvider, recipientId);
    yield put({
      type: recipientActions.DELETE_RECIPIENT_SUCCESS,
      payload: recipientId,
    });
  } catch (error) {
    error.name = 'Could not delete Recipient';
    yield put({ type: recipientActions.DELETE_RECIPIENT_ERROR, payload: error });
  }
}

function* updateRecipient({ payload }) {
  try {
    const { offRampProvider, recipientId, data } = payload || {};
    if (!offRampProvider || !recipientId) {
      throw new Error('offRampProvider and recipientId are required');
    }

    const updatedRecipient = yield call(API.updateRecipient, offRampProvider, recipientId, data);

    yield put({
      type: recipientActions.UPDATE_RECIPIENT_SUCCESS,
      payload: updatedRecipient,
    });

    yield delay(300);
    const recipients = yield call(API.getRecipients);
    yield put({
      type: recipientActions.GET_RECIPIENTS_SUCCESS,
      payload: recipients,
    });
  } catch (error) {
    const errorPayload = {
      name: 'Could not update Recipient',
      message: error.message,
      statusCode: error.statusCode,
      traceId: error.traceId,
      response: error.response,
      data: error.data,
      ...error,
    };
    yield put({ type: recipientActions.UPDATE_RECIPIENT_ERROR, payload: errorPayload });
  }
}

function* parseRecipientDocument({ payload }) {
  try {
    const { file, currencyCode, userType, rail, schema } = payload || {};
    if (!file) {
      throw new Error('file is required for parse recipient document');
    }

    const validationError = getDocumentUploadValidationError(file);
    if (validationError) {
      throw new Error(validationError);
    }
    const formData = new FormData();
    formData.append('currencyCode', currencyCode || '');
    formData.append('userType', userType || '');
    formData.append('rail', rail || '');
    if (schema && typeof schema === 'object') {
      formData.append('schema', JSON.stringify(schema));
    }

    const data = yield call(API.parseDocument, file, formData);
    yield put({
      type: recipientActions.PARSE_RECIPIENT_DOCUMENT_SUCCESS,
      payload: data,
    });
  } catch (error) {
    const errorPayload = {
      name: 'Parse recipient document',
      message: error.message || 'Parse failed',
      statusCode: error.statusCode,
      response: error.response,
      ...error,
    };
    yield put({
      type: recipientActions.PARSE_RECIPIENT_DOCUMENT_ERROR,
      payload: errorPayload,
    });
  }
}

const sagas = [
  [recipientActions.GET_RECIPIENTS, getRecipients],
  [recipientActions.GET_RECIPIENT, getRecipient],
  [recipientActions.ADD_RECIPIENT, addRecipient],
  [recipientActions.UPDATE_RECIPIENT, updateRecipient],
  [recipientActions.DELETE_RECIPIENT, deleteRecipient],
  [recipientActions.PARSE_RECIPIENT_DOCUMENT, parseRecipientDocument],
];

export default sagas;
